import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import i18n from './index';
import {
  DEFAULT_LANGUAGE,
  LANGUAGE_STORAGE_KEY,
  SUPPORTED_LANGUAGES,
  getLanguage,
  type SupportedLanguage,
} from './config';
import { clinicalTerm } from './clinicalSafety';
import {
  formatLocaleCurrency,
  formatLocaleDate,
  formatLocaleDateTime,
  formatLocaleNumber,
} from './format';

/**
 * Current language plus the setter every switcher uses.
 *
 * The preference is stored per device rather than per account, because it has to be
 * changeable *before* anyone is authenticated — a Telugu-only patient cannot be expected to
 * navigate an English login screen to find the setting. Once a `preferredLanguage` column
 * exists on the staff and patient records, `setLanguage` is where the profile write goes;
 * the local write stays as the pre-auth path and the offline fallback.
 */
export function useAppLanguage() {
  const { i18n: instance } = useTranslation();
  const code = instance.resolvedLanguage || instance.language || DEFAULT_LANGUAGE;

  const setLanguage = useCallback((next: string) => {
    void i18n.changeLanguage(next);
    try {
      localStorage.setItem(LANGUAGE_STORAGE_KEY, next);
    } catch {
      // Private mode or blocked storage — the in-memory change still applies for this session.
    }
  }, []);

  const language: SupportedLanguage = useMemo(() => getLanguage(code), [code]);

  return {
    /** e.g. `'te'` */
    code: language.code,
    language,
    languages: SUPPORTED_LANGUAGES,
    isDefault: language.code === DEFAULT_LANGUAGE,
    setLanguage,
  };
}

/**
 * Formatters already bound to the active language, so a component never has to pass the
 * language code around. `clinical` variants stay `en-IN` — see `format.ts`.
 */
export function useLocaleFormat() {
  const { code } = useAppLanguage();

  return useMemo(
    () => ({
      date: (v: string | number | Date | null | undefined, o?: Intl.DateTimeFormatOptions) =>
        formatLocaleDate(v, code, o),
      dateTime: (v: string | number | Date | null | undefined) => formatLocaleDateTime(v, code),
      number: (v: number | null | undefined, o?: Intl.NumberFormatOptions) =>
        formatLocaleNumber(v, code, o),
      currency: (v: number | null | undefined) => formatLocaleCurrency(v, code),
    }),
    [code],
  );
}

/**
 * Renders a clinical term with its English canonical form always attached.
 *
 *   const ct = useClinicalTerm();
 *   ct('vitals.bloodPressure', 'Blood Pressure')
 *     en -> 'Blood Pressure'
 *     te -> 'రక్తపోటు (Blood Pressure)'
 *
 * The English argument is mandatory and is what renders if the gloss is missing, so a gap in
 * the Telugu glossary can never produce a clinical term a patient or pharmacist cannot match
 * against their English paperwork.
 */
export function useClinicalTerm() {
  const { t } = useTranslation('clinical');

  return useCallback(
    (key: string, english: string): string => {
      const translated = t(key, { defaultValue: '' });
      return clinicalTerm(translated, english);
    },
    [t],
  );
}
